import ButtonT from '@components/tailwind-components/Button/ButtonT';
import InputT from '@components/tailwind-components/Input/InputT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import { PROVISION_STATUS_ROUTE } from '@constants/routes';
import { useModal } from '@hooks/modal/useModal';
import { useCompanySettingsActions } from '@hooks/settings/company/useCompanyActions';
import clsx from 'clsx';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';

export interface CompanyLifecycleModalProps {
  companyId: string;
  companyName: string;
}

type TLifecycleAction = 'deactivate' | 'deprovision';

interface TLifecycleActionCard {
  action: TLifecycleAction;
  titleKey: string;
  descriptionKey: string;
  isSuperAdminOnly?: boolean;
}

// * Reached only when the caller has already confirmed the company is
// * superAdmin-eligible for deprovision - see GeneralSettings.tsx. The
// * superAdmin marker here is informational, not a gate.
const LIFECYCLE_ACTION_CARDS: TLifecycleActionCard[] = [
  {
    action: 'deactivate',
    titleKey: 'general-settings.lifecycle-modal.action-deactivate-label',
    descriptionKey:
      'general-settings.lifecycle-modal.action-deactivate-description',
  },
  {
    action: 'deprovision',
    titleKey: 'general-settings.lifecycle-modal.action-deprovision-label',
    descriptionKey:
      'general-settings.lifecycle-modal.action-deprovision-description',
    isSuperAdminOnly: true,
  },
];

export const CompanyLifecycleModal: React.FC<CompanyLifecycleModalProps> = ({
  companyId,
  companyName,
}) => {
  const { hideModal } = useModal();
  const { deactivateCompany } = useCompanySettingsActions();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [action, setAction] = useState<TLifecycleAction>('deactivate');
  const [confirmationText, setConfirmationText] = useState('');

  const isDeprovision = action === 'deprovision';
  const trimmedConfirmationText = confirmationText.trim();
  const isConfirmed = !isDeprovision || trimmedConfirmationText === companyName;

  // * Switching cards must not leave a stale confirmation behind - the button
  // * would otherwise stay enabled with no re-confirmation for the new choice.
  const handleSelectAction = (nextAction: TLifecycleAction) => {
    if (nextAction === action) {
      return;
    }

    setAction(nextAction);
    setConfirmationText('');
  };

  // * Teardown is watched on the provisioning curtain rather than run from here:
  // * this settings page belongs to a company that is about to go inactive, so the
  // * request is issued by the status page it routes to.
  const handleSubmit = () => {
    if (isDeprovision) {
      hideModal();
      navigate(generatePath(PROVISION_STATUS_ROUTE, { companyId }), {
        state: { startTeardown: true },
      });

      return;
    }

    deactivateCompany();
    hideModal();
  };

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        <p className='text-sm italic mb-4'>
          {t('general-settings.lifecycle-modal.description')}
        </p>
        <LabelT text={t('general-settings.lifecycle-modal.action-label')} />
        <div className='flex flex-col gap-2 mt-1 mb-4'>
          {LIFECYCLE_ACTION_CARDS.map((card) => (
            <button
              key={card.action}
              type='button'
              onClick={() => handleSelectAction(card.action)}
              className={clsx(
                'text-left p-3 rounded-md border transition-colors',
                action === card.action
                  ? 'border-primary-base bg-primary-lightest'
                  : 'border-gray-200 hover:border-gray-400',
              )}>
              <div className='flex items-center gap-2'>
                <span className='text-sm font-medium text-primary-base'>
                  {t(card.titleKey)}
                </span>
                {card.isSuperAdminOnly && (
                  <span className='text-pico font-medium px-2 py-0.5 rounded-full bg-gray-200 text-gray-600'>
                    {t('general-settings.lifecycle-modal.superadmin-badge')}
                  </span>
                )}
              </div>
              <p className='text-tiny text-gray-500 mt-1'>
                {t(card.descriptionKey)}
              </p>
            </button>
          ))}
        </div>
        {isDeprovision && (
          <div className='flex flex-col'>
            <LabelT
              text={t('general-settings.lifecycle-modal.confirm-label', {
                companyName,
              })}
            />
            <InputT
              type='text'
              autoFocus
              options={{
                value: confirmationText,
                onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                  setConfirmationText(event.target.value),
              }}
            />
          </div>
        )}
      </div>
      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={() => hideModal()}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='danger'
          size='xs'
          rounding='md'
          disabled={!isConfirmed}
          onClick={handleSubmit}>
          {t('general-settings.lifecycle-modal.submit')}
        </ButtonT>
      </div>
    </div>
  );
};

export default CompanyLifecycleModal;
